/** A linha dobrada abaixo da resposta, Ver consulta: abre o SQL em bloco
 *  monoespaçado com o objetivo, botão de copiar e a quantidade de linhas lidas.
 *  Fechada por padrão: a resposta fica sempre visível, o trabalho fica
 *  disponível para quem quiser conferir. */
import { useState } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Check, ChevronDown, Copy, Database } from 'lucide-react'
import { useI18n } from '@/core/i18n/useI18n'
import type { ConsultaDaResposta } from './api'
import { useCopiar } from './useCopiar'

export function ConsultaSql({ consulta }: { consulta: ConsultaDaResposta }) {
  const { t } = useI18n()
  const [aberta, setAberta] = useState(false)
  const { copiado, copiar } = useCopiar()

  return (
    <Box sx={{ mt: 1 }}>
      <ButtonBase
        onClick={() => setAberta((a) => !a)}
        aria-expanded={aberta}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 0.75,
          py: 0.375,
          ml: -0.75,
          borderRadius: 1.5,
          color: 'text.secondary',
          fontSize: '0.75rem',
          fontWeight: 600,
          '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
          '&.Mui-focusVisible': { boxShadow: 'var(--focus-ring)' },
        }}
      >
        <Database size={13} strokeWidth={2} aria-hidden />
        <span>{t(aberta ? 'assistente.consulta.ocultar' : 'assistente.consulta.ver')}</span>
        <Box
          component="span"
          sx={{
            px: 0.75, py: 0.125, borderRadius: 1, fontSize: '0.6875rem', fontWeight: 600,
            bgcolor: 'var(--color-surface-strong)', color: 'text.secondary', fontVariantNumeric: 'tabular-nums',
          }}
        >
          {t('assistente.consulta.linhas', { count: consulta.linhas })}
        </Box>
        <ChevronDown
          size={13}
          aria-hidden
          style={{ transform: aberta ? 'none' : 'rotate(-90deg)', transition: 'transform var(--transition-base)' }}
        />
      </ButtonBase>

      <Collapse in={aberta} timeout={200}>
        <Box sx={{ mt: 0.75, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          {consulta.objetivo && (
            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled' }}>
                {t('assistente.consulta.objetivo')}
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{consulta.objetivo}</Typography>
            </Box>
          )}
          <Box sx={{ position: 'relative', bgcolor: 'var(--color-surface-strong)' }}>
            <Tooltip title={t(copiado ? 'assistente.consulta.copiado' : 'assistente.consulta.copiar')}>
              <IconButton
                size="small"
                onClick={() => copiar(consulta.sql)}
                aria-label={t('assistente.consulta.copiar')}
                sx={{ position: 'absolute', top: 4, right: 4, color: copiado ? 'success.main' : 'text.secondary' }}
              >
                {copiado ? <Check size={14} strokeWidth={2.4} /> : <Copy size={14} strokeWidth={2} />}
              </IconButton>
            </Tooltip>
            <Box
              component="pre"
              sx={{
                m: 0,
                px: 1.5,
                py: 1.25,
                pr: 5,
                fontFamily: 'var(--font-mono)',
                fontSize: '0.75rem',
                lineHeight: 1.5,
                color: 'text.primary',
                overflowX: 'auto',
                whiteSpace: 'pre',
              }}
            >
              {consulta.sql}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}
